import {Controller} from "@hotwired/stimulus"

// Long-press + swipe gestures for messages and comments in Flutter WebView.
// - Long-press released without swiping → opens the centered context menu
//   (dispatches "context-menu:open" with `contextMenuSrc`).
// - After long-press, a right-swipe past threshold → dispatches "reply:start"
//   (same payload as the dropdown reply button), if allowSwipe is true.
//
// Values:
//   threshold         – swipe distance (px) to trigger reply (default 80)
//   longPressDuration – ms to hold before activating (default 400)
//   recordId          – the record UUID (message or comment)
//   recordType        – "Message" or "Comment"
//   authorName        – display name of the author
//   content           – text content (truncated)
//   contextMenuSrc    – URL of the turbo-frame returning the context menu content
//   allowSwipe        – whether right-swipe triggers reply (default true)
export default class extends Controller {
    static targets = ["bubble"]
    static values = {
        threshold: {type: Number, default: 80},
        longPressDuration: {type: Number, default: 400},
        recordId: String,
        recordType: {type: String, default: "Message"},
        authorName: String,
        content: String,
        contextMenuSrc: String,
        allowSwipe: {type: Boolean, default: true}
    }

    connect() {
        this._startX = 0
        this._startY = 0
        this._swiping = false
        this._selected = false
        this._longPressTimer = null
        this._translateX = 0

        this._onTouchStart = this._handleTouchStart.bind(this)
        this._onTouchMove = this._handleTouchMove.bind(this)
        this._onTouchEnd = this._handleTouchEnd.bind(this)

        this.element.addEventListener("touchstart", this._onTouchStart, {passive: true})
        this.element.addEventListener("touchmove", this._onTouchMove, {passive: false})
        this.element.addEventListener("touchend", this._onTouchEnd, {passive: true})
        this.element.addEventListener("touchcancel", this._onTouchEnd, {passive: true})

        this._createReplyHint()
    }

    disconnect() {
        this._clearLongPress()
        this.element.removeEventListener("touchstart", this._onTouchStart)
        this.element.removeEventListener("touchmove", this._onTouchMove)
        this.element.removeEventListener("touchend", this._onTouchEnd)
        this.element.removeEventListener("touchcancel", this._onTouchEnd)
        this._removeReplyHint()
    }

    _handleTouchStart(event) {
        if (event.touches.length !== 1) return

        const touch = event.touches[0]
        this._startX = touch.clientX
        this._startY = touch.clientY
        this._swiping = false
        this._translateX = 0

        this._longPressTimer = setTimeout(() => {
            this._selected = true
            this._vibrate()
            this._highlightBubble(true)
        }, this.longPressDurationValue)
    }

    _handleTouchMove(event) {
        if (event.touches.length !== 1) {
            this._cancel()
            return
        }

        const touch = event.touches[0]
        const dx = touch.clientX - this._startX
        const dy = touch.clientY - this._startY

        if (!this._swiping && !this._selected) {
            if (Math.abs(dy) > 10) {
                this._clearLongPress()
                return
            }
            if (Math.abs(dx) > 10) {
                this._clearLongPress()
                this._swiping = true
            }
            return
        }

        if (this._selected && !this._swiping) {
            if (Math.abs(dx) > 10) {
                this._swiping = true
            }
            return
        }

        if (!this._swiping) return
        if (dx < 0) return
        if (!this.allowSwipeValue) return

        event.preventDefault()

        const clamped = Math.min(dx, this.thresholdValue * 1.5)
        this._translateX = clamped
        this._applyTranslate(clamped)

        const progress = Math.min(dx / this.thresholdValue, 1)
        this._updateReplyHint(progress, clamped)
    }

    _handleTouchEnd() {
        this._clearLongPress()

        const swipedEnough = this._swiping && this._translateX >= this.thresholdValue && this.allowSwipeValue
        const longPressReleased = this._selected && !this._swiping

        if (swipedEnough) {
            this._vibrate()
            this._triggerReply()
        } else if (longPressReleased && this.hasContextMenuSrcValue && this.contextMenuSrcValue) {
            this._triggerContextMenu()
        }

        this._resetVisuals()
        this._swiping = false
        this._selected = false
        this._translateX = 0
    }

    _triggerReply() {
        window.dispatchEvent(new CustomEvent("reply:start", {
            detail: {
                recordId: this.recordIdValue,
                recordType: this.recordTypeValue,
                authorName: this.authorNameValue,
                content: this.contentValue
            }
        }))
    }

    _triggerContextMenu() {
        // Emit reactable hints when present so reactions_picker_controller can
        // stack its popup on top of the context menu (see docs/REACTIONS_SYSTEM.md §7).
        const reactableType = this.element.dataset.reactableType ||
            (this.recordTypeValue && ["Message", "Comment"].includes(this.recordTypeValue) ? this.recordTypeValue : null)
        const reactableId = this.element.dataset.reactableId || this.recordIdValue

        document.dispatchEvent(new CustomEvent("context-menu:open", {
            bubbles: true,
            detail: {
                src: this.contextMenuSrcValue,
                reactableType: reactableType,
                reactableId: reactableId
            }
        }))
    }

    _applyTranslate(px) {
        const bubble = this._getBubble()
        if (!bubble) return
        bubble.style.transform = `translateX(${px}px)`
        bubble.style.transition = "none"
    }

    _resetVisuals() {
        const bubble = this._getBubble()
        if (bubble) {
            bubble.style.transition = "transform 0.2s ease"
            bubble.style.transform = ""
        }
        this._highlightBubble(false)
        this._hideReplyHint()
    }

    _highlightBubble(on) {
        const bubble = this._getBubble()
        if (!bubble) return
        bubble.style.boxShadow = on ? "0 0 0 2px rgba(14, 165, 233, 0.4)" : ""
    }

    _getBubble() {
        if (this.hasBubbleTarget) return this.bubbleTarget
        return this.element.querySelector("[data-swipe-reply-target='bubble']") ||
            this.element.querySelector(".inline-block.rounded-xl")
    }

    _vibrate() {
        if (navigator.vibrate) navigator.vibrate(20)
    }

    _clearLongPress() {
        if (this._longPressTimer) {
            clearTimeout(this._longPressTimer)
            this._longPressTimer = null
        }
    }

    _cancel() {
        this._clearLongPress()
        this._resetVisuals()
        this._swiping = false
        this._selected = false
        this._translateX = 0
    }

    _createReplyHint() {
        this._replyHint = document.createElement("div")
        Object.assign(this._replyHint.style, {
            position: "absolute",
            left: "-32px",
            top: "50%",
            transform: "translateY(-50%)",
            opacity: "0",
            transition: "opacity 0.15s ease",
            pointerEvents: "none",
            zIndex: "1"
        })

        const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
        svg.setAttribute("viewBox", "0 0 24 24")
        svg.setAttribute("fill", "none")
        svg.setAttribute("stroke", "currentColor")
        svg.setAttribute("stroke-width", "2")
        svg.setAttribute("stroke-linecap", "round")
        svg.setAttribute("stroke-linejoin", "round")
        Object.assign(svg.style, {width: "20px", height: "20px", color: "#0ea5e9"})

        const polyline = document.createElementNS("http://www.w3.org/2000/svg", "polyline")
        polyline.setAttribute("points", "9 17 4 12 9 7")
        const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
        path.setAttribute("d", "M20 18v-2a4 4 0 0 0-4-4H4")
        svg.appendChild(polyline)
        svg.appendChild(path)
        this._replyHint.appendChild(svg)

        this.element.style.position = "relative"
        this.element.appendChild(this._replyHint)
    }

    _updateReplyHint(progress, translateX) {
        if (!this._replyHint) return
        this._replyHint.style.opacity = `${progress}`
        this._replyHint.style.left = `${translateX - 32}px`

        if (progress >= 1) {
            this._replyHint.querySelector("svg").style.color = "#059669"
        } else {
            this._replyHint.querySelector("svg").style.color = "#0ea5e9"
        }
    }

    _hideReplyHint() {
        if (!this._replyHint) return
        this._replyHint.style.opacity = "0"
        this._replyHint.style.left = "-32px"
    }

    _removeReplyHint() {
        if (this._replyHint?.parentNode) {
            this._replyHint.parentNode.removeChild(this._replyHint)
        }
        this._replyHint = null
    }
}
